import axios from "axios";
import type { PracticeSubmission } from "@huosu/shared";
import type {
  CommerceDashboard,
  ContentCategory,
  Course,
  DesktopDownloadCenter,
  DesktopInstallationInput,
  DesktopInstallationRecord,
  DesktopInstallationStatus,
  DesktopPlatform,
  FollowReadingClip,
  LearnerActivityDashboard,
  AiAssistantContextReply,
  KnowledgeArticle,
  Lesson,
  MembershipPlan,
  OrderRecord,
  PaymentMethod,
  UserCoupon,
  VoiceAsset,
  VoiceCode,
  SpeechRateValue,
  WordEntry,
  LearnerProfile,
  AdaptiveLearningPlan,
  AdaptiveTrainingBatch,
  AiGenerationPricing,
  AiCreditOrder,
  SystemLearningLibrary,
  LearningProfileConfig
} from "@huosu/shared";

export interface AdaptiveLearningHome {
  profile: LearnerProfile;
  needsOnboarding: boolean;
  needsPlacement: boolean;
  plan: AdaptiveLearningPlan | null;
  libraries: SystemLearningLibrary[];
  manualCourseSelectionEnabled: boolean;
  algorithm: string;
  config: LearningProfileConfig;
  recommendations: AdaptiveLearningPlan["items"];
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || "",
  timeout: 15000,
  withCredentials: true
});

export const USER_AUTH_EXPIRED_EVENT = "huosu-user-auth-expired";
let userSessionCleanupInFlight: Promise<void> | undefined;

/**
 * 失效令牌必须同时清理 localStorage 和 HttpOnly 学员 Cookie。专用接口只处理
 * 学员会话，避免学习端自动降级为游客时把已登录的后台管理员一并退出。
 */
export async function clearExpiredUserSession() {
  const hadToken = Boolean(localStorage.getItem("huosu_user_token"));
  localStorage.removeItem("huosu_user_token");
  if (hadToken && typeof window !== "undefined") window.dispatchEvent(new Event(USER_AUTH_EXPIRED_EVENT));
  if (userSessionCleanupInFlight) return userSessionCleanupInFlight;
  const cleanup = api.post("/api/v1/auth/user-logout").then(() => undefined).catch(() => undefined);
  userSessionCleanupInFlight = cleanup;
  try {
    await cleanup;
  } finally {
    if (userSessionCleanupInFlight === cleanup) userSessionCleanupInFlight = undefined;
  }
}

export async function loadSentenceGrammar(ids: string[], create = true, retry = false): Promise<Array<import('@huosu/shared').GrammarResult & { id: string }>> {
  const response = create ? await api.post('/api/v1/sentences/grammar', { ids, retry }) : await api.get('/api/v1/sentences/grammar', { params: { ids: ids.join(',') } });
  if (!response.data.success) throw new Error('语法加载未完成');
  return response.data.data;
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("huosu_user_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = Number(error?.response?.status || 0);
    const url = String(error?.config?.url || "");
    if (status === 401 && !url.includes("/api/v1/auth/user-logout") && localStorage.getItem("huosu_user_token")) {
      void clearExpiredUserSession();
    }
    return Promise.reject(error);
  }
);

export interface BootstrapData {
  courses: Course[];
  categories: ContentCategory[];
  categoryList: ContentCategory[];
  words: WordEntry[];
  knowledge: KnowledgeArticle[];
  plans: MembershipPlan[];
  study: {
    todayMinutes: number;
    totalMinutes: number;
    quotaMinutes: number;
    completedSentences: number;
    thousandHourProgress: number;
  };
  guideAudiences: string[];
}

const now = new Date().toISOString();
const offlineCategories: ContentCategory[] = [
  {
    id: "root-scenarios",
    name: "生活场景",
    slug: "life-scenarios",
    kind: "root",
    description: "家庭、学校和日常出行里的高频英文。",
    icon: "home",
    color: "#10b981",
    sortOrder: 10,
    status: "enabled",
    level: 1,
    path: ["root-scenarios"],
    pathName: "生活场景",
    tags: ["生活", "口语"],
    audience: "英语学习者",
    difficulty: "初级",
    aiProfile: {
      promptFocus: "生成自然、短小、适合听写的生活英语句子。",
      defaultTopic: "家庭日常",
      targetWords: ["time", "get up", "farm"],
      grammarFocus: ["一般现在时", "动词短语"],
      sentenceCount: 20,
      lessonTitleTemplate: "{topic} 高频句"
    },
    seoTitle: "生活场景英语",
    seoDescription: "生活场景英语听写和打字练习。",
    stats: { courses: 1, lessons: 1, sentences: 4, words: 8, knowledge: 0 },
    createdAt: now,
    updatedAt: now
  },
  {
    id: "scenario-family",
    parentId: "root-scenarios",
    name: "家庭亲子",
    slug: "family-english",
    kind: "scenario",
    description: "亲子开口、起床、吃饭、学习和睡前表达。",
    icon: "family",
    color: "#22d3ee",
    sortOrder: 10,
    status: "enabled",
    level: 2,
    path: ["root-scenarios", "scenario-family"],
    pathName: "生活场景 / 家庭亲子",
    tags: ["亲子", "家庭"],
    audience: "家长/小学生",
    difficulty: "初级",
    aiProfile: {
      promptFocus: "生成家长和孩子能马上开口使用的英文短句。",
      defaultTopic: "早晨起床",
      targetWords: ["get up", "practice", "habit"],
      grammarFocus: ["It is time to", "一般现在时"],
      sentenceCount: 20,
      lessonTitleTemplate: "{topic}"
    },
    seoTitle: "家庭亲子英语",
    seoDescription: "家庭亲子英语核心句子练习。",
    stats: { courses: 1, lessons: 1, sentences: 4, words: 8, knowledge: 0 },
    createdAt: now,
    updatedAt: now
  }
];

const offlineCourses: Course[] = [
  {
    id: "course-family-english",
    title: "亲子开口英语 2000 句",
    subtitle: "从家庭高频场景开始，让孩子和家长每天都能开口。",
    category: "家庭亲子",
    categoryId: "scenario-family",
    categoryPath: ["root-scenarios", "scenario-family"],
    audience: "家长/小学生",
    level: "初级",
    joinedCount: 0,
    isMemberOnly: false,
    cover: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='640' height='400' viewBox='0 0 640 400'%3E%3Crect width='640' height='400' fill='%2306080a'/%3E%3Ccircle cx='188' cy='152' r='72' fill='%2322d3ee' opacity='.72'/%3E%3Ccircle cx='378' cy='188' r='94' fill='%23a78bfa' opacity='.7'/%3E%3Ctext x='64' y='318' fill='white' font-size='44' font-family='Arial'%3E%E7%81%AB%E9%80%9F%E8%8B%B1%E8%AF%AD%3C/text%3E%3C/svg%3E",
    tags: ["亲子", "启蒙", "高频口语"],
    progress: 0,
    lessons: [
      {
        id: "lesson-family-1",
        title: "早晨起床",
        description: "围绕起床、习惯和家庭表达建立第一批键盘记忆。",
        sentenceCount: 4,
        isFreeTrial: true,
        sentences: [
          {
            id: "sent-offline-1",
            english: "It is time to get up.",
            chinese: "该起床了。",
            phonetic: "/ɪt ɪz taɪm tuː ɡet ʌp/",
            grammarTags: ["It is time to...", "动词短语 get up"],
            structure: [
              { label: "形式主语", text: "It", explain: "用于引出时间或情境。" },
              { label: "表语", text: "is time to get up", explain: "说明现在该做什么。" }
            ],
            keywords: ["time", "get up"]
          },
          {
            id: "sent-offline-2",
            english: "A small habit can make speaking easier.",
            chinese: "一个小习惯能让开口更容易。",
            phonetic: "/ə smɔːl ˈhæbɪt kæn meɪk ˈspiːkɪŋ ˈiːziər/",
            grammarTags: ["情态动词 can", "make + object + adjective"],
            structure: [
              { label: "主语", text: "A small habit", explain: "说明带来变化的原因。" },
              { label: "谓语", text: "can make", explain: "表示能够产生影响。" },
              { label: "宾补", text: "speaking easier", explain: "说明开口这件事变得更容易。" }
            ],
            keywords: ["habit", "speaking", "easier"]
          },
          {
            id: "sent-offline-3",
            english: "I practice English for ten minutes every day.",
            chinese: "我每天练习十分钟英语。",
            phonetic: "/aɪ ˈpræktɪs ˈɪŋɡlɪʃ fər ten ˈmɪnɪts ˈevri deɪ/",
            grammarTags: ["一般现在时", "时间状语"],
            structure: [
              { label: "主语", text: "I", explain: "动作的发出者。" },
              { label: "谓语", text: "practice", explain: "表示每天重复的动作。" },
              { label: "宾语", text: "English", explain: "练习的对象。" },
              { label: "状语", text: "for ten minutes every day", explain: "说明练习时长和频率。" }
            ],
            keywords: ["practice", "minutes", "every day"]
          },
          {
            id: "sent-offline-4",
            english: "My grandpa lives on a farm.",
            chinese: "我的爷爷住在一个农场里。",
            phonetic: "/maɪ ˈɡrænpɑː lɪvz ɑːn ə fɑːrm/",
            grammarTags: ["一般现在时", "第三人称单数", "地点状语"],
            structure: [
              { label: "主语", text: "My grandpa", explain: "说明动作的发出者。" },
              { label: "谓语", text: "lives", explain: "第三人称单数动词形式。" },
              { label: "状语", text: "on a farm", explain: "说明居住地点。" }
            ],
            keywords: ["grandpa", "lives", "farm"]
          }
        ]
      }
    ]
  }
];

const offlineWords: WordEntry[] = [
  {
    id: "word-time",
    word: "time",
    usPhonetic: "/taɪm/",
    ukPhonetic: "/taɪm/",
    meanings: [{ partOfSpeech: "n.", zh: "时间；时刻", en: "what is measured in minutes, hours, days, and years" }],
    forms: { plural: "times" },
    examples: [{ en: "It is time to get up.", zh: "该起床了。", analysis: "It is time to... 表示该做某事。" }],
    phrases: ["it is time to", "on time"],
    synonyms: ["moment"],
    derivatives: ["timely"],
    wordbooks: ["小学核心词", "KET基础词"],
    mastery: "new"
  },
  {
    id: "word-farm",
    word: "farm",
    usPhonetic: "/fɑːrm/",
    ukPhonetic: "/fɑːm/",
    meanings: [{ partOfSpeech: "n.", zh: "农场", en: "an area of land used for growing crops or keeping animals" }],
    forms: { plural: "farms" },
    examples: [{ en: "My grandpa lives on a farm.", zh: "我的爷爷住在一个农场里。" }],
    phrases: ["on a farm", "farm animals"],
    synonyms: ["ranch"],
    derivatives: ["farmer", "farming"],
    wordbooks: ["小学核心词", "KET基础词"],
    mastery: "new"
  }
];

const offlineBootstrap: BootstrapData = {
  courses: offlineCourses,
  categories: offlineCategories,
  categoryList: offlineCategories,
  words: offlineWords,
  knowledge: [],
  plans: [
    {
      key: "free",
      name: "免费版",
      price: 0,
      dailyMinutes: 30,
      speechAssessments: 10,
      benefits: ["每天30分钟练习", "核心学习模式体验", "离线种子课可用"],
      status: "active",
      sortOrder: 10
    }
  ],
  study: {
    todayMinutes: 0,
    totalMinutes: 0,
    quotaMinutes: 30,
    completedSentences: 0,
    thousandHourProgress: 0
  },
  guideAudiences: ["零基础", "亲子/小学生", "中高考", "职场商务", "口语提升"]
};

// 登录恢复、练习提交和页面初始化都可能在同一时刻请求目录。共享同一个在途
// 请求，避免重复下载同一份课程摘要并让按钮看起来“卡住”。
let bootstrapInFlight: Promise<BootstrapData> | undefined;

export async function getBootstrap(): Promise<BootstrapData> {
  if (bootstrapInFlight) return bootstrapInFlight;
  const request = (async () => {
    try {
      const { data } = await api.get("/api/v1/bootstrap", { params: { compact: "1" } });
      return data.data;
  } catch (error) {
    if (Number((error as any)?.response?.status || 0) === 401) {
      await clearExpiredUserSession();
      const { data } = await api.get("/api/v1/bootstrap", { params: { compact: "1" } });
      return data.data;
    }
    // Authenticated statistics must never be replaced by the zeroed guest
      // bootstrap when a refresh request times out or the tab resumes offline.
      if (localStorage.getItem("huosu_user_token")) throw error;
      return offlineBootstrap;
    }
  })();
  bootstrapInFlight = request;
  try {
    return await request;
  } finally {
    if (bootstrapInFlight === request) bootstrapInFlight = undefined;
  }
}

function offlineLearnerActivity(): LearnerActivityDashboard {
  const sentence = offlineCourses[0]?.lessons[0]?.sentences[0];
  const word = offlineWords[0];
  const nowIso = new Date().toISOString();
  return {
    personalWordLibraries: [
      {
        id: "offline-my-words",
        userId: "guest",
        type: "word",
        name: "我的单词库",
        description: "收藏、错词和自建词条统一进入这里。",
        visibility: "private",
        source: "system_seed",
        items: word
          ? [{ id: "offline-word-item", sourceId: word.id, text: word.word, translation: word.meanings[0]?.zh, tags: ["favorite"], mastery: "new", createdAt: nowIso }]
          : [],
        createdAt: nowIso,
        updatedAt: nowIso
      }
    ],
    personalSentenceLibraries: [
      {
        id: "offline-my-sentences",
        userId: "guest",
        type: "sentence",
        name: "我的句库",
        description: "收藏、听写错句和自建句子统一进入这里。",
        visibility: "private",
        source: "system_seed",
        items: sentence
          ? [{ id: "offline-sentence-item", sourceId: sentence.id, text: sentence.english, translation: sentence.chinese, tags: ["favorite"], mastery: "new", createdAt: nowIso }]
          : [],
        createdAt: nowIso,
        updatedAt: nowIso
      }
    ],
    followReadingClips: sentence
      ? [
          {
            id: "offline-follow-family",
            title: "早晨起床跟读",
            description: "围绕起床、习惯、家庭表达做短句跟读。",
            cover: offlineCourses[0].cover,
            sourceType: "text",
            tags: ["亲子", "起床", "跟读"],
            level: "初级",
            sentenceIds: [sentence.id],
            sentences: [sentence],
            favoriteCount: 0,
            createdAt: nowIso
          }
        ]
      : [],
    favoriteWords: word ? [{ id: "offline-word-fav", sourceId: word.id, text: word.word, translation: word.meanings[0]?.zh, tags: ["favorite"], mastery: "new", createdAt: nowIso }] : [],
    favoriteSentences: sentence ? [{ id: "offline-sentence-fav", sourceId: sentence.id, text: sentence.english, translation: sentence.chinese, tags: ["favorite"], mastery: "new", createdAt: nowIso }] : [],
    checkins: { todayChecked: false, streak: 0, records: [] },
    review: { today: 0, weakWords: 0, mistakeCount: 0, masteredCount: 0 },
    learningHistory: { totalPackages: 0, completedPackages: 0, learnedWords: 0, learnedContentSentences: 0, learnedSentences: 0, totalPracticeCount: 0, packages: [], recent: [] },
    learningResume: null,
    aiAssistantPrompts: ["这句话怎么拆成主谓宾？", "哪些词容易听错，怎么记？", "帮我生成 3 个同场景替换句。"]
  };
}

export async function getLearnerActivity(): Promise<LearnerActivityDashboard> {
  const { data } = await api.get("/api/v1/learner/activity");
  if (!data.success) throw new Error(data.error || "个人学习数据加载失败");
  return data.data;
}

export async function openDailyAdaptiveTraining(): Promise<{ triggered: boolean; reason: string; plan: AdaptiveLearningPlan | null; batch: AdaptiveTrainingBatch | null }> {
  const { data } = await api.post("/api/v1/learning/adaptive-training-packs/daily-open");
  if (!data.success) throw new Error(data.error || "今日任务初始化失败");
  return data.data;
}

export async function getPracticeLocationBySentenceId(sentenceId: string): Promise<{ courseId: string; lessonId: string; sentenceId: string }> {
  const { data } = await api.get(`/api/v1/learning/practice-location/${encodeURIComponent(sentenceId)}`);
  if (!data.success) throw new Error(data.error || "推荐学习内容不存在或已下架");
  return data.data;
}

export async function getFollowReadingClips(): Promise<FollowReadingClip[]> {
  const { data } = await api.get("/api/v1/follow-reading");
  return data.data;
}

export async function createPersonalLibrary(payload: { type: "word" | "sentence"; name: string; description?: string }) {
  const { data } = await api.post("/api/v1/learner/libraries", payload);
  if (!data.success) throw new Error(data.error || "创建个人库失败");
  return data.data;
}

export async function addPersonalLibraryItem(libraryId: string, payload: { sourceId?: string; text: string; translation?: string; note?: string; tags?: string[] }) {
  const { data } = await api.post(`/api/v1/learner/libraries/${libraryId}/items`, payload);
  if (!data.success) throw new Error(data.error || "加入个人库失败");
  return data.data;
}

export async function toggleFavorite(payload: { targetType: "word" | "sentence"; targetId?: string; text?: string; translation?: string }) {
  const { data } = await api.post("/api/v1/learner/favorites", payload);
  if (!data.success) throw new Error(data.error || "收藏失败");
  return data.data;
}

export async function learnerCheckin(): Promise<{ activity: LearnerActivityDashboard; message: string }> {
  const { data } = await api.post("/api/v1/learner/checkin");
  if (!data.success) throw new Error(data.error || "打卡失败");
  return data.data;
}

export async function askAiAssistant(payload: { sentenceId?: string; question?: string; word?: string }): Promise<AiAssistantContextReply> {
  const { data } = await api.post("/api/v1/learner/ai-assistant", payload);
  if (!data.success) throw new Error(data.error || "AI助手暂不可用");
  return data.data;
}

export async function getLoginCaptcha(): Promise<{ captchaId: string; captchaImage: string }> {
  const { data } = await api.get("/api/v1/auth/captcha");
  return data.data;
}

export async function sendCode(account: string) {
  const { data } = await api.post("/api/v1/auth/send-code", { account, type: "sms" });
  if (!data.success) throw new Error(data.error || "验证码发送失败");
  return data.data;
}

export async function getPhoneAccountStatus(phone: string): Promise<{ registered: boolean; needsNickname: boolean }> {
  const { data } = await api.post("/api/v1/auth/phone-status", { phone });
  if (!data.success) throw new Error(data.error || "手机号识别失败");
  return data.data;
}

export async function loginByPhone(phone: string, code: string, nickname?: string) {
  const { data } = await api.post("/api/v1/auth/login-by-phone", { phone, code, nickname });
  if (data.success && data.data?.accessToken) localStorage.setItem("huosu_user_token", data.data.accessToken);
  return data;
}

export async function getMe() {
  const { data } = await api.get("/api/v1/auth/me");
  return data.data;
}

export async function logout() {
  try {
    const { data } = await api.post("/api/v1/auth/logout");
    return data.data;
  } finally {
    // Local logout must always complete, even when the network or session endpoint fails.
    localStorage.removeItem("huosu_user_token");
  }
}

export async function getPracticeLesson(lessonId: string): Promise<{ course: Course; lesson: Lesson }> {
  try {
    const { data } = await api.get(`/api/v1/practice/${encodeURIComponent(lessonId)}`, { params: { compact: "1" } });
    if (!data.success || !data.data?.lesson) throw new Error(data.error || "课节内容暂不可用");
    return data.data;
  } catch {
    const course = offlineCourses.find((item) => item.lessons.some((lesson) => lesson.id === lessonId));
    const lesson = course?.lessons.find((item) => item.id === lessonId);
    if (course && lesson) return { course, lesson };
    throw new Error("课节内容暂不可用");
  }
}

export async function getCourseDetail(courseId: string): Promise<Course> {
  const { data } = await api.get(`/api/v1/courses/${encodeURIComponent(courseId)}`, { params: { compact: "1" } });
  if (!data.success || !data.data) throw new Error(data.error || "课包内容暂不可用");
  return data.data;
}

export async function submitPracticeAnswer(payload: PracticeSubmission) {
  const { data } = await api.post("/api/v1/learning/practice-answer", payload);
  if (!data.success) throw new Error(data.error || "学习记录保存失败");
  return data.data;
}

export async function getReviewSummary() {
  try {
    const { data } = await api.get("/api/v1/learning/review");
    return data.data;
  } catch {
    return { dueToday: 0, mistakeCount: 0, newWordCount: 0, masteredCount: 0 };
  }
}

export async function getAdaptiveLearningHome(): Promise<AdaptiveLearningHome> {
  const { data } = await api.get("/api/v1/learning/adaptive-home");
  if (!data.success) throw new Error(data.error || "智能学习计划暂不可用");
  return data.data;
}

export async function saveLearnerProfile(payload: Partial<LearnerProfile>): Promise<AdaptiveLearningHome> {
  const { data } = await api.post("/api/v1/learning/profile", payload);
  if (!data.success) throw new Error(data.error || "学习画像保存失败");
  return data.data;
}

export async function submitPlacementAssessment(skillScores: Partial<LearnerProfile["skillScores"]>): Promise<AdaptiveLearningHome> {
  const { data } = await api.post("/api/v1/learning/placement", { skillScores });
  if (!data.success) throw new Error(data.error || "水平测评提交失败");
  return data.data;
}

export async function regenerateAdaptivePlan(): Promise<AdaptiveLearningPlan> {
  const { data } = await api.post("/api/v1/learning/adaptive-plan/regenerate");
  if (!data.success) throw new Error(data.error || "计划生成失败");
  return data.data;
}

export async function startAdaptiveTrainingPackGeneration(contentCount = 60): Promise<AdaptiveTrainingBatch> {
  const { data } = await api.post("/api/v1/learning/adaptive-training-packs", { contentCount });
  if (!data.success) throw new Error(data.error || "训练包生成任务启动失败");
  return data.data;
}

export async function getAdaptiveTrainingGenerationPricing(): Promise<AiGenerationPricing> {
  const { data } = await api.get("/api/v1/learning/adaptive-training-packs/options");
  if (!data.success) throw new Error(data.error || "AI生成积分配置读取失败");
  return data.data;
}

export async function getLatestAdaptiveTrainingPackBatch(): Promise<AdaptiveTrainingBatch | null> {
  const { data } = await api.get("/api/v1/learning/adaptive-training-packs/latest");
  if (!data.success) throw new Error(data.error || "训练包任务读取失败");
  return data.data;
}

export async function getAdaptiveTrainingPackBatches(offset = 0): Promise<AdaptiveTrainingBatch[]> {
  const { data } = await api.get("/api/v1/learning/adaptive-training-packs", { params: { offset } });
  if (!data.success) throw new Error(data.error || "训练包历史读取失败");
  return data.data || [];
}

export async function getSavedTrainingPack(packId: string): Promise<import("@huosu/shared").AdaptiveTrainingPack> {
  const { data } = await api.get(`/api/v1/learning/saved-training-pack/${encodeURIComponent(packId)}`);
  if (!data.success) throw new Error(data.error || "训练包暂不可用");
  return data.data;
}

export async function getPublicMembershipPlans(): Promise<MembershipPlan[]> {
  const { data } = await api.get("/api/v1/commerce/plans");
  if (!data.success || !Array.isArray(data.data)) throw new Error("套餐暂时读取失败，请重试。价格不会以演示数据替代。");
  return data.data;
}

export async function getAdaptiveTrainingPackBatch(batchId: string): Promise<AdaptiveTrainingBatch> {
  const { data } = await api.get(`/api/v1/learning/adaptive-training-packs/${encodeURIComponent(batchId)}`);
  if (!data.success) throw new Error(data.error || "训练包任务读取失败");
  return data.data;
}

export async function getStudyDictionary(words: string[]): Promise<import("./study-annotations").StudyDictionaryEntry[]> {
  const { data } = await api.get("/api/v1/words/study", { params: { words: words.join(",") } });
  if (!data.success || !Array.isArray(data.data)) throw new Error("逐词资料加载失败");
  return data.data;
}

export async function searchWords(q: string): Promise<WordEntry[]> {
  try {
    const { data } = await api.get("/api/v1/words/search", { params: { q } });
    if (!data.success || !Array.isArray(data.data)) throw new Error("词典资料暂不可用");
    return data.data;
  } catch {
    const keyword = q.toLowerCase();
    return offlineWords
      .filter((entry) => entry.word.toLowerCase().includes(keyword))
      .sort((left, right) => Number(right.word.toLowerCase() === keyword) - Number(left.word.toLowerCase() === keyword));
  }
}

export async function getCommerceMe(): Promise<CommerceDashboard> {
  const { data } = await api.get("/api/v1/commerce/me");
  return data.data;
}

export async function createAiCreditRechargeOrder(packageKey: string): Promise<AiCreditOrder> {
  const { data } = await api.post("/api/v1/commerce/ai-credits/orders", { packageKey });
  if (!data.success) throw new Error(data.error || "AI积分订单创建失败");
  return data.data;
}

export async function payAiCreditRechargeOrder(orderNo: string): Promise<OrderRecord> {
  const { data } = await api.post(`/api/v1/commerce/ai-credits/orders/${encodeURIComponent(orderNo)}/pay`);
  if (!data.success) throw new Error(data.error || "AI积分充值失败");
  return data.data;
}

export async function closeAiCreditRechargeOrder(orderNo: string): Promise<OrderRecord> {
  const { data } = await api.post(`/api/v1/commerce/ai-credits/orders/${encodeURIComponent(orderNo)}/close`);
  if (!data.success) throw new Error(data.error || "积分订单取消失败");
  return data.data;
}

export async function getOpenApiStatus(): Promise<{ publicEnabled: boolean; docsUrl: string; specUrl: string; endpointCount: number }> {
  const { data } = await api.get("/api/v1/openapi/status");
  return data.data;
}

export async function getDesktopDownloadCenter(platform?: DesktopPlatform): Promise<DesktopDownloadCenter> {
  const { data } = await api.get("/api/v1/desktop/download-center", { params: platform ? { platform } : {} });
  return data.data;
}

export async function getDesktopInstallationStatus(platform: DesktopPlatform, browserDeviceId?: string): Promise<DesktopInstallationStatus> {
  const { data } = await api.get("/api/v1/desktop/installation-status", { params: { platform, browserDeviceId } });
  return data.data;
}

export async function registerDesktopInstallation(payload: DesktopInstallationInput): Promise<DesktopInstallationRecord> {
  const { data } = await api.post("/api/v1/desktop/installations/register", payload);
  if (!data.success) throw new Error(data.error || "桌面端安装登记失败");
  return data.data;
}

export async function trackDesktopDownload(versionId: string) {
  const { data } = await api.post(`/api/v1/desktop/downloads/${encodeURIComponent(versionId)}/track`);
  return data.data;
}

export async function ensureTtsAudio(payload: { sentenceId?: string; text: string; voiceCode: VoiceCode; speechRate?: SpeechRateValue; verifyCached?: boolean; priority?: "interactive" | "prefetch" }): Promise<{
  audioAsset: VoiceAsset;
  cached: boolean;
  storageMode: "oss" | "local";
  provider: string;
}> {
  // TTS includes provider generation and OSS persistence. Give this endpoint a
  // dedicated window instead of inheriting the generic 15 second API timeout.
  const timeout = payload.priority === "prefetch" ? 20_000 : 35_000;
  const { data } = await api.post("/api/v1/learning/tts", payload, { timeout });
  if (!data.success) throw new Error(data.error || "TTS 生成失败");
  return data.data;
}

export async function assessSpeech(payload: { sentenceId?: string; text?: string; audioBase64: string; mimeType?: string }): Promise<{
  target: string;
  transcript: string;
  transcriptMatchScore: number;
  provider: string;
  model: string;
  fallbackUsed: boolean;
  audioSeconds: number;
  notice: string;
}> {
  const { data } = await api.post("/api/v1/learning/speech-assessment", payload, { timeout: 35_000 });
  if (!data.success) throw new Error(data.error || "口语识别失败");
  return data.data;
}

export async function createMembershipOrder(payload: { planKey: string; couponCode?: string; inviteCode?: string }): Promise<OrderRecord> {
  const { data } = await api.post("/api/v1/commerce/orders", payload);
  if (!data.success) throw new Error(data.error || "创建订单失败");
  return data.data;
}

export async function payMembershipOrder(orderNo: string, payType: PaymentMethod = "wechat"): Promise<OrderRecord> {
  const { data } = await api.post(`/api/v1/commerce/orders/${orderNo}/pay`, { payType });
  if (!data.success) throw new Error(data.error || "支付失败");
  return data.data;
}

export async function getMembershipOrder(orderNo: string): Promise<OrderRecord> {
  const { data } = await api.get(`/api/v1/commerce/orders/${orderNo}`);
  if (!data.success) throw new Error(data.error || "订单查询失败");
  return data.data;
}

export async function closeMembershipOrder(orderNo: string): Promise<OrderRecord> {
  const { data } = await api.post(`/api/v1/commerce/orders/${orderNo}/close`);
  if (!data.success) throw new Error(data.error || "订单关闭失败");
  return data.data;
}

export async function claimCoupon(templateCode: string): Promise<UserCoupon> {
  const { data } = await api.post("/api/v1/commerce/coupons/claim", { templateCode });
  if (!data.success) throw new Error(data.error || "领取优惠券失败");
  return data.data;
}

export async function updateAffiliatePayout(payload: { alipayAccount?: string; alipayRealName?: string }) {
  const { data } = await api.post("/api/v1/commerce/affiliate/payout", payload);
  if (!data.success) throw new Error(data.error || "保存收款信息失败");
  return data.data;
}

export async function requestWithdrawal(amount: number) {
  const { data } = await api.post("/api/v1/commerce/affiliate/withdrawals", { amount });
  if (!data.success) throw new Error(data.error || "提现申请失败");
  return data.data;
}
